import { useSignal } from "@preact/signals";
import { useEffect, useRef } from "preact/hooks";
import { rpc } from "../../rpc";
import {
  appendNotifications,
  markAllNotificationsRead,
  markNotificationRead,
  notifications,
  notificationsCursor,
  notificationsLoaded,
  notificationsLoading,
  setNotifications,
  setNotificationsCursor,
  setNotificationsLoading,
  setUnreadCount,
  unreadCount,
} from "../../stores/notifications";
import { IcoBell } from "../icons";
import { NotificationDropdown } from "./notification-dropdown";

const POLL_INTERVAL_MS = 60_000; // Poll every 60 seconds

export function NotificationBell() {
  const isOpen = useSignal(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Fetch unread count on mount and poll periodically
  useEffect(() => {
    fetchUnreadCount();

    const interval = setInterval(fetchUnreadCount, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    if (!isOpen.value) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        isOpen.value = false;
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen.value]);

  // Load notifications when dropdown opens
  useEffect(() => {
    if (isOpen.value && !notificationsLoaded.value) {
      fetchNotifications();
    }
  }, [isOpen.value]);

  async function fetchUnreadCount() {
    try {
      const result = await rpc.notifications.getUnreadCount({});
      setUnreadCount(result.count);
    } catch (err) {
      console.error("Failed to fetch unread count:", err);
    }
  }

  async function fetchNotifications() {
    if (notificationsLoading.value) return;

    setNotificationsLoading(true);
    try {
      const result = await rpc.notifications.list({ limit: 20 });
      setNotifications(result.notifications);
      setNotificationsCursor(result.nextCursor);
      setUnreadCount(result.unreadCount);
    } catch (err) {
      console.error("Failed to fetch notifications:", err);
    } finally {
      setNotificationsLoading(false);
    }
  }

  async function loadMore() {
    if (notificationsLoading.value || !notificationsCursor.value) return;

    setNotificationsLoading(true);
    try {
      const result = await rpc.notifications.list({
        limit: 20,
        cursor: notificationsCursor.value,
      });
      appendNotifications(result.notifications);
      setNotificationsCursor(result.nextCursor);
    } catch (err) {
      console.error("Failed to load more notifications:", err);
    } finally {
      setNotificationsLoading(false);
    }
  }

  async function handleMarkRead(notificationId: string) {
    // Optimistically update UI
    markNotificationRead(notificationId);

    try {
      await rpc.notifications.markRead({ notificationId });
    } catch (err) {
      console.error("Failed to mark notification as read:", err);
      // Refetch to sync state
      fetchNotifications();
    }
  }

  async function handleMarkAllRead() {
    // Optimistically update UI
    markAllNotificationsRead();

    try {
      await rpc.notifications.markAllRead({});
    } catch (err) {
      console.error("Failed to mark all as read:", err);
      // Refetch to sync state
      fetchNotifications();
    }
  }

  async function handleNotificationClick(notification: {
    id: string;
    viewUrl: string;
    type: string;
    targetId: string | null;
    workspaceId: string;
  }) {
    handleMarkRead(notification.id);
    isOpen.value = false;

    let url = notification.viewUrl;

    // For recognition notifications, try to resolve to the actual submission page
    // (the stored viewUrl may just point to /feed if no submission existed at creation time)
    if (
      notification.type === "recognition" &&
      notification.targetId &&
      !url.includes("submission=")
    ) {
      try {
        const result = await rpc.valueRecognitions.resolveViewUrl({
          recognitionId: notification.targetId,
          workspaceId: notification.workspaceId,
        });
        if (result.viewUrl) {
          url = result.viewUrl;
        }
      } catch {
        // Fall through to original URL
      }
    }

    window.location.href = url;
  }

  const count = unreadCount.value;

  return (
    <div ref={containerRef} class="relative">
      <button
        type="button"
        class="relative p-2 rounded-full hover:bg-surface-hover transition-colors"
        aria-label={`Notifications${count > 0 ? ` (${count} unread)` : ""}`}
        onClick={() => {
          isOpen.value = !isOpen.value;
        }}
      >
        <IcoBell class="size-5 text-text-muted" />
        {count > 0 && (
          <span class="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 flex items-center justify-center rounded-full bg-red-500 text-white text-xs font-medium">
            {count > 99 ? "99+" : count}
          </span>
        )}
      </button>

      {isOpen.value && (
        <NotificationDropdown
          notifications={notifications.value}
          loading={notificationsLoading.value}
          hasMore={notificationsCursor.value !== null}
          onMarkAllRead={handleMarkAllRead}
          onLoadMore={loadMore}
          onNotificationClick={handleNotificationClick}
        />
      )}
    </div>
  );
}
